import { useEffect, useRef } from 'react';

import axios from 'axios';

import { HEADERS } from 'constants/requests';
import { DEFAULT_HEADERS } from 'constants/url';
import { checkRefreshToken } from 'data-providers/authProvider';
import handler401 from 'utils/handler401';
import { isTokenExpired } from 'utils/JWTUtils';
import * as localStorage from 'utils/local-storage';
import appRequest, { authRequest, traefikRequest } from 'utils/request';
import { requestTracer } from 'utils/request-tracer';
import sleep from 'utils/sleep';
import { uuidObject } from 'utils/uuid';

const RETRY_DELAY = 1000;
const MAX_REFRESH_ATTEMPTS = 2;

const Interceptors = () => {
  const state = useRef({
    isRefreshing: false, // состояние активности рефреша
    refreshWaiter: null, // останавливаем остальные запросы, пока активен рефреш
  }).current;

  // Создаём промис, чтобы запросы могли дождаться завершения рефреша
  const createRefreshWaiter = () => {
    let resolve;

    const promise = new Promise((res) => {
      return (resolve = res);
    });

    return { promise, resolve };
  };

  // Если в данный момент идёт рефреш - ставим задержку на промис
  const awaitRefreshing = async () => {
    while (state.isRefreshing) {
      await sleep(RETRY_DELAY);
    }
  };

  // Завершаем ожидание - снимаем флаг и резолвим промис
  const resolveRefreshWaiter = () => {
    state.isRefreshing = false;

    if (state.refreshWaiter) {
      state.refreshWaiter.resolve();
      state.refreshWaiter = null;
    }
  };

  // Выполняем обновление токена
  const performTokenRefresh = async () => {
    try {
      for (let attempt = 1; attempt <= MAX_REFRESH_ATTEMPTS; attempt++) {
        const newToken = await checkRefreshToken();

        if (newToken?.token) {
          return newToken;
        }

        // Повторная попытка рефреша
        if (attempt < MAX_REFRESH_ATTEMPTS) {
          await sleep(RETRY_DELAY);
        }
      }

      // Разлогин, если обе попытки оказались неудачными
      handler401();

      return null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Refresh token failed:', error);
      throw error;
    } finally {
      resolveRefreshWaiter();
    }
  };

  useEffect(() => {
    const agents = [appRequest, authRequest, traefikRequest];
    const interceptors = [];

    // Интерсептор для всех исходящих запросов
    const handleRequest = async (config) => {
      await awaitRefreshing();

      const stored = localStorage.getItem('token');

      if (stored?.token) {
        if (isTokenExpired(stored.token)) {
          // Токен истёк - запускаем рефреш
          if (!state.isRefreshing) {
            state.isRefreshing = true;
            state.refreshWaiter = createRefreshWaiter();

            const newToken = await performTokenRefresh();

            // eslint-disable-next-line max-depth
            if (!newToken) {
              return Promise.reject(new Error('Token refresh failed'));
            }

            config.headers[HEADERS.AUTHORIZATION] = `Bearer ${newToken.token}`;
          } else {
            // Рефреш уже идёт - ждём и берём новый токен
            await state.refreshWaiter.promise;

            const freshStored = localStorage.getItem('token');

            config.headers[HEADERS.AUTHORIZATION] = `Bearer ${freshStored?.token || ''}`;
          }
        } else {
          // Токен ещё валиден - подставляем его
          config.headers[HEADERS.AUTHORIZATION] = `Bearer ${stored.token}`;
        }
      }

      // Добавляем остальные хедеры
      config.headers[HEADERS.CORRELATION_ID] = uuidObject.get();
      config.headers = {
        ...DEFAULT_HEADERS,
        ...config.headers,
      };

      return config;
    };

    // Интерсептор для ошибок ответа
    const handleResponseError = async (error) => {
      await awaitRefreshing();

      const { config, response } = error;

      if (!response || !config) {
        return Promise.reject(error);
      }

      await requestTracer.info(response);

      // Обработка 401 ошибки
      if (response.status === 401 && config && !config._retry) {
        config._retryCount = config._retryCount || 0;

        if (config._retryCount >= MAX_REFRESH_ATTEMPTS) {
          handler401();

          return Promise.reject(error);
        }

        config._retryCount += 1;

        const stored = localStorage.getItem('token');

        if (stored?.token && !isTokenExpired(stored.token)) {
          // Токен ещё валиден, но сервер вернул 401 - разлогиниваем
          handler401();

          return Promise.reject(error);
        }

        if (!state.isRefreshing) {
          // Запускаем рефреш
          state.isRefreshing = true;
          state.refreshWaiter = createRefreshWaiter();

          const newToken = await performTokenRefresh();

          if (!newToken) {
            return Promise.reject(new Error('Token refresh failed'));
          }

          // Обновляем хедер и повторяем запрос
          config.headers[HEADERS.AUTHORIZATION] = `Bearer ${newToken.token}`;
          config._retry = true;

          return axios(config);
        }
        // Если рефреш уже идёт - ждём его завершения и повторяем запрос
        await state.refreshWaiter.promise;

        const freshStored = localStorage.getItem('token');

        config.headers[HEADERS.AUTHORIZATION] = `Bearer ${freshStored?.token || ''}`;
        config._retry = true;

        return axios(config);
      }

      return Promise.reject(error);
    };

    // Навешиваем интерсепторы на все агенты
    agents.forEach((agent) => {
      const reqInterceptor = agent.interceptors.request.use(handleRequest);
      const resInterceptor = agent.interceptors.response.use(
        requestTracer.info.bind(requestTracer),
        handleResponseError
      );

      interceptors.push({ request: reqInterceptor, response: resInterceptor });
    });

    // Снимаем интерсепторы при размонтировании
    return () => {
      agents.forEach((agent, i) => {
        agent.interceptors.request.eject(interceptors[i].request);
        agent.interceptors.response.eject(interceptors[i].response);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default Interceptors;
